import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import TextSnippetIcon from "@mui/icons-material/TextSnippet";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import BalanceIcon from "@mui/icons-material/Balance";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import SendIcon from "@mui/icons-material/Send";
import StorageIcon from "@mui/icons-material/Storage";
import ArticleIcon from "@mui/icons-material/Article";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import { makeStyles } from "@mui/styles";
import {
  parseReconciliationFile,
  submitReconciliationDecisions,
} from "../../../services/Import";
import { getUser } from "../../../Utils/Common";
import FormAlert from "../../../components/FormAlert";

// ─── Styled Components ────────────────────────────────────────────────────────

const useStyles = makeStyles({
  fileContainer: {
    padding: "20px",
    marginTop: "15px",
    marginBottom: "15px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    border: "2px dashed #ccc",
    borderRadius: "5px",
    cursor: "pointer",
    "&:hover": {
      borderColor: "#1976d2",
      backgroundColor: alpha("#1976d2", 0.02),
    },
  },
  selectedFile: {
    padding: "10px",
    marginTop: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "50%",
  },
  fileInput: {
    display: "none",
  },
});

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 1.5,
  textTransform: "none",
  fontWeight: 600,
  boxShadow: "0 4px 12px 0 rgba(0,0,0,0.1)",
}));

const SummaryCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius * 1.5,
  textAlign: "center",
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
}));

const BulkActionBar = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  padding: theme.spacing(1.5, 2),
  borderRadius: theme.shape.borderRadius * 1.5,
  backgroundColor: alpha(theme.palette.primary.main, 0.04),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
  flexWrap: "wrap",
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  "& .MuiTableCell-head": {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    fontWeight: 700,
    fontSize: "0.78rem",
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: theme.palette.primary.dark,
  },
}));

// ─── Status config ────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  MATCHED: {
    color: "success",
    bgColor: alpha("#2e7d32", 0.06),
    borderColor: alpha("#2e7d32", 0.2),
    label: "Matched",
    icon: <CheckCircleOutlineIcon fontSize="small" />,
  },
  MISMATCHED: {
    color: "warning",
    bgColor: alpha("#ed6c02", 0.06),
    borderColor: alpha("#ed6c02", 0.25),
    label: "Mismatched",
    icon: <ErrorOutlineIcon fontSize="small" />,
  },
  NOT_FOUND: {
    color: "error",
    bgColor: alpha("#d32f2f", 0.05),
    borderColor: alpha("#d32f2f", 0.2),
    label: "Not Found",
    icon: <HelpOutlineIcon fontSize="small" />,
  },
};

const DEFAULT_REASON = {
  SYSTEM: "System quantity accepted as correct after physical count review.",
  EXCEL:
    "Physical count accepted as correct. System quantity updated to match manual count.",
};

const STEP_UPLOAD = "upload";
const STEP_REVIEW = "review";
const STEP_DONE = "done";

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

function StockReconciliation({ setOpenReconciliationForm }) {
  const classes = useStyles();

  const [step, setStep] = useState(STEP_UPLOAD);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isParsing, setIsParsing] = useState(false);
  const [reconciliationResult, setReconciliationResult] = useState(null);
  const [decisions, setDecisions] = useState({});
  const [sessionNote, setSessionNote] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [displayAlert, setDisplayAlert] = useState(false);
  const [alertData, setAlertData] = useState({ type: "", message: "" });
  const [doneMessage, setDoneMessage] = useState("");

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (ext === "xlsx" || ext === "xls") {
      setSelectedFile(file);
      setDisplayAlert(false);
    } else {
      showAlert("error", "Please select a valid Excel file (.xlsx or .xls)");
      setSelectedFile(null);
    }
  };

  const handleParse = async () => {
    if (!selectedFile) return;
    setIsParsing(true);
    setDisplayAlert(false);
    const result = await parseReconciliationFile(selectedFile);
    setIsParsing(false);
    if (!result) {
      showAlert(
        "error",
        "Failed to parse file. Please check the file format and try again.",
      );
      return;
    }
    const initialDecisions = {};
    result.lines.forEach((line) => {
      if (line.status === "MISMATCHED") {
        initialDecisions[line.importId] = { acceptedSource: null, reason: "" };
      }
    });
    setReconciliationResult(result);
    setDecisions(initialDecisions);
    setStep(STEP_REVIEW);
  };

  const handleDecisionChange = (importId, field, value) => {
    setDecisions((prev) => ({
      ...prev,
      [importId]: { ...prev[importId], [field]: value },
    }));
  };

  // When clicking individual row button — set source and pre-fill reason if empty
  const handleRowDecision = (importId, source) => {
    setDecisions((prev) => ({
      ...prev,
      [importId]: {
        acceptedSource: source,
        // only pre-fill if reason is currently blank
        reason: prev[importId]?.reason?.trim()
          ? prev[importId].reason
          : DEFAULT_REASON[source],
      },
    }));
  };

  // Bulk: accept all mismatched as SYSTEM correct
  const handleAcceptAllAsSystem = () => {
    const updated = {};
    getMismatchedLines().forEach((line) => {
      updated[line.importId] = {
        acceptedSource: "SYSTEM",
        // keep any custom reason the user already typed, else use default
        reason:
          decisions[line.importId]?.reason?.trim() || DEFAULT_REASON.SYSTEM,
      };
    });
    setDecisions((prev) => ({ ...prev, ...updated }));
  };

  // Bulk: accept all mismatched as EXCEL correct
  const handleAcceptAllAsExcel = () => {
    const updated = {};
    getMismatchedLines().forEach((line) => {
      updated[line.importId] = {
        acceptedSource: "EXCEL",
        reason:
          decisions[line.importId]?.reason?.trim() || DEFAULT_REASON.EXCEL,
      };
    });
    setDecisions((prev) => ({ ...prev, ...updated }));
  };

  const getMismatchedLines = () =>
    reconciliationResult?.lines?.filter((l) => l.status === "MISMATCHED") || [];

  const allDecisionsMade = () =>
    getMismatchedLines().every((line) => {
      const d = decisions[line.importId];
      return d && d.acceptedSource && d.reason && d.reason.trim().length > 0;
    });

  const decisionsCompletedCount = () =>
    Object.values(decisions).filter((d) => d.acceptedSource && d.reason?.trim())
      .length;

  const handleSubmit = async () => {
    if (!allDecisionsMade()) {
      showAlert(
        "error",
        "Please select a source and provide a reason for every mismatched item.",
      );
      return;
    }
    setIsSubmitting(true);
    setDisplayAlert(false);
    const user = getUser();
    const decisionList = getMismatchedLines().map((line) => ({
      importId: line.importId,
      productCode: line.productCode,
      systemQty: line.systemQty,
      excelQty: line.excelQty,
      acceptedSource: decisions[line.importId].acceptedSource,
      reason: decisions[line.importId].reason.trim(),
    }));
    const submission = {
      submittedBy: user?.username || "unknown",
      sessionNote: sessionNote.trim() || null,
      decisions: decisionList,
    };
    const result = await submitReconciliationDecisions(submission);
    setIsSubmitting(false);
    if (result?.success) {
      setDoneMessage(result.message);
      setStep(STEP_DONE);
    } else {
      showAlert(
        "error",
        result?.message || "Submission failed. Please try again.",
      );
    }
  };

  const showAlert = (type, message) => {
    setAlertData({ type, message });
    setDisplayAlert(true);
  };

  const resetAll = () => {
    setStep(STEP_UPLOAD);
    setSelectedFile(null);
    setIsParsing(false);
    setReconciliationResult(null);
    setDecisions({});
    setSessionNote("");
    setDisplayAlert(false);
    setDoneMessage("");
  };

  // ─── Render helpers ────────────────────────────────────────────────────────

  const renderDiffChip = (difference) => {
    if (difference === null || difference === undefined) return "—";
    const abs = Math.abs(difference).toFixed(2);
    if (difference > 0)
      return (
        <Chip
          label={`+${abs}`}
          size="small"
          sx={{
            backgroundColor: alpha("#2e7d32", 0.1),
            color: "#2e7d32",
            fontWeight: 700,
          }}
        />
      );
    if (difference < 0)
      return (
        <Chip
          label={`−${abs}`}
          size="small"
          sx={{
            backgroundColor: alpha("#d32f2f", 0.1),
            color: "#d32f2f",
            fontWeight: 700,
          }}
        />
      );
    return (
      <Chip
        label="0"
        size="small"
        sx={{ backgroundColor: alpha("#2e7d32", 0.1), color: "#2e7d32" }}
      />
    );
  };

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER: UPLOAD STEP
  // ─────────────────────────────────────────────────────────────────────────

  const renderUploadStep = () => (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <input
          accept=".xlsx, .xls"
          className={classes.fileInput}
          id="reconciliation-file-input"
          type="file"
          onChange={handleFileSelect}
          disabled={isParsing}
        />
        <label htmlFor="reconciliation-file-input">
          <Paper className={classes.fileContainer} component="div">
            <UploadFileIcon fontSize="large" color="primary" />
            <Typography variant="body1" mt={1}>
              {isParsing ? "Processing..." : "Click to browse or drag and drop"}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Upload the manually counted stock Excel file (.xlsx or .xls)
            </Typography>
          </Paper>
        </label>
      </Grid>

      {selectedFile && (
        <Grid item xs={12}>
          <Paper
            className={classes.selectedFile}
            style={{ backgroundColor: "#e8fae0" }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <TextSnippetIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="body2" noWrap>
                {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
              </Typography>
            </Box>
            {!isParsing && (
              <IconButton size="small" onClick={() => setSelectedFile(null)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Paper>
          {isParsing && (
            <Box sx={{ width: "50%", mt: 1 }}>
              <LinearProgress />
              <Typography variant="caption" color="textSecondary">
                Comparing with system data...
              </Typography>
            </Box>
          )}
        </Grid>
      )}

      <Grid item xs={12}>
        {displayAlert && (
          <FormAlert type={alertData.type} message={alertData.message} />
        )}
      </Grid>

      <Grid item xs={12}>
        <Grid container justifyContent="space-between">
          <Grid item xs={12} sm={4}>
            <ActionButton
              fullWidth
              variant="contained"
              startIcon={
                isParsing ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <CloudUploadIcon />
                )
              }
              onClick={handleParse}
              disabled={!selectedFile || isParsing}
            >
              {isParsing ? "Processing..." : "Parse & Compare"}
            </ActionButton>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER: REVIEW STEP
  // ─────────────────────────────────────────────────────────────────────────

  const renderReviewStep = () => {
    const {
      totalItemsInExcel,
      matchedItems,
      mismatchedItems,
      notFoundInSystem,
      lines,
    } = reconciliationResult;

    return (
      <Grid container spacing={3}>
        {/* Summary Cards */}
        <Grid item xs={6} sm={3}>
          <SummaryCard>
            <Typography variant="h4" fontWeight="bold" color="primary">
              {totalItemsInExcel}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Total in Excel
            </Typography>
          </SummaryCard>
        </Grid>
        <Grid item xs={6} sm={3}>
          <SummaryCard>
            <Typography variant="h4" fontWeight="bold" color="success.main">
              {matchedItems}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Matched
            </Typography>
          </SummaryCard>
        </Grid>
        <Grid item xs={6} sm={3}>
          <SummaryCard>
            <Typography variant="h4" fontWeight="bold" color="warning.main">
              {mismatchedItems}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Mismatched
            </Typography>
          </SummaryCard>
        </Grid>
        <Grid item xs={6} sm={3}>
          <SummaryCard>
            <Typography variant="h4" fontWeight="bold" color="error.main">
              {notFoundInSystem}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Not Found
            </Typography>
          </SummaryCard>
        </Grid>

        {/* Session Note */}
        <Grid item xs={12}>
          <TextField
            label="Session Note (optional)"
            placeholder="e.g. Monthly physical count — February 2026"
            variant="outlined"
            size="small"
            fullWidth
            value={sessionNote}
            onChange={(e) => setSessionNote(e.target.value)}
          />
        </Grid>

        {/* ── Bulk Action Bar ─────────────────────────────────────────────── */}
        {mismatchedItems > 0 && (
          <Grid item xs={12}>
            <BulkActionBar>
              <DoneAllIcon color="primary" fontSize="small" />
              <Typography
                variant="body2"
                fontWeight={600}
                color="primary"
                sx={{ mr: 1 }}
              >
                Bulk Actions ({mismatchedItems} mismatched items):
              </Typography>

              <Tooltip title="Mark ALL mismatched items as System quantity correct. Default reason is pre-filled — edit any row individually if needed.">
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  startIcon={<StorageIcon fontSize="small" />}
                  onClick={handleAcceptAllAsSystem}
                  sx={{ textTransform: "none", fontWeight: 600 }}
                >
                  Accept All as System Correct
                </Button>
              </Tooltip>

              <Tooltip title="Mark ALL mismatched items as Excel quantity correct. System will be adjusted. Default reason is pre-filled — edit any row individually if needed.">
                <Button
                  size="small"
                  variant="contained"
                  color="warning"
                  startIcon={<ArticleIcon fontSize="small" />}
                  onClick={handleAcceptAllAsExcel}
                  sx={{ textTransform: "none", fontWeight: 600 }}
                >
                  Accept All as Excel Correct
                </Button>
              </Tooltip>

              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ ml: "auto" }}
              >
                Default reasons are pre-filled. Edit any row individually if
                needed.
              </Typography>
            </BulkActionBar>
          </Grid>
        )}

        {/* Reconciliation Table */}
        <Grid item xs={12}>
          <TableContainer
            component={Paper}
            sx={{ borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.07)" }}
          >
            <Table size="small" stickyHeader>
              <StyledTableHead>
                <TableRow>
                  <TableCell>Status</TableCell>
                  <TableCell>Product Code</TableCell>
                  <TableCell>Item Name</TableCell>
                  <TableCell>Brand</TableCell>
                  <TableCell align="right">System Qty</TableCell>
                  <TableCell align="right">Excel Qty</TableCell>
                  <TableCell align="center">Difference</TableCell>
                  <TableCell sx={{ minWidth: 200 }}>
                    Accept As Correct
                  </TableCell>
                  <TableCell sx={{ minWidth: 260 }}>Reason</TableCell>
                </TableRow>
              </StyledTableHead>
              <TableBody>
                {lines.map((line, idx) => {
                  const config = STATUS_CONFIG[line.status];
                  const decision = decisions[line.importId] || null;

                  return (
                    <TableRow
                      key={idx}
                      sx={{
                        backgroundColor: config.bgColor,
                        borderLeft: `3px solid ${config.borderColor}`,
                        "&:hover": { filter: "brightness(0.97)" },
                      }}
                    >
                      {/* Status */}
                      <TableCell>
                        <Chip
                          icon={config.icon}
                          label={config.label}
                          color={config.color}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>

                      {/* Product Code */}
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          fontFamily="monospace"
                        >
                          {line.productCode}
                        </Typography>
                      </TableCell>

                      {/* Item Name */}
                      <TableCell>
                        <Typography variant="body2">
                          {line.itemName || (
                            <em style={{ color: "#aaa" }}>—</em>
                          )}
                        </Typography>
                      </TableCell>

                      {/* Brand */}
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {line.brand || "—"}
                        </Typography>
                      </TableCell>

                      {/* System Qty */}
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          color={
                            line.status === "NOT_FOUND"
                              ? "textDisabled"
                              : "inherit"
                          }
                        >
                          {line.systemQty !== null &&
                          line.systemQty !== undefined ? (
                            line.systemQty.toLocaleString()
                          ) : (
                            <em style={{ color: "#bbb" }}>N/A</em>
                          )}
                        </Typography>
                      </TableCell>

                      {/* Excel Qty */}
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>
                          {line.excelQty !== null
                            ? line.excelQty.toLocaleString()
                            : "—"}
                        </Typography>
                      </TableCell>

                      {/* Difference */}
                      <TableCell align="center">
                        {renderDiffChip(line.difference)}
                      </TableCell>

                      {/* Accept As Correct */}
                      <TableCell>
                        {line.status === "MISMATCHED" ? (
                          <Stack direction="row" spacing={1}>
                            <Tooltip
                              title={`Keep System Qty: ${line.systemQty}`}
                            >
                              <Button
                                size="small"
                                variant={
                                  decision?.acceptedSource === "SYSTEM"
                                    ? "contained"
                                    : "outlined"
                                }
                                color="primary"
                                startIcon={<StorageIcon fontSize="small" />}
                                onClick={() =>
                                  handleRowDecision(line.importId, "SYSTEM")
                                }
                                sx={{
                                  textTransform: "none",
                                  fontSize: "0.75rem",
                                  minWidth: "unset",
                                }}
                              >
                                System
                              </Button>
                            </Tooltip>
                            <Tooltip title={`Use Excel Qty: ${line.excelQty}`}>
                              <Button
                                size="small"
                                variant={
                                  decision?.acceptedSource === "EXCEL"
                                    ? "contained"
                                    : "outlined"
                                }
                                color="warning"
                                startIcon={<ArticleIcon fontSize="small" />}
                                onClick={() =>
                                  handleRowDecision(line.importId, "EXCEL")
                                }
                                sx={{
                                  textTransform: "none",
                                  fontSize: "0.75rem",
                                  minWidth: "unset",
                                }}
                              >
                                Excel
                              </Button>
                            </Tooltip>
                          </Stack>
                        ) : (
                          <Typography variant="caption" color="textSecondary">
                            {line.status === "MATCHED"
                              ? "No action needed"
                              : "—"}
                          </Typography>
                        )}
                      </TableCell>

                      {/* Reason */}
                      <TableCell>
                        {line.status === "MISMATCHED" ? (
                          <TextField
                            size="small"
                            placeholder="Reason for discrepancy..."
                            variant="outlined"
                            value={decision?.reason || ""}
                            onChange={(e) =>
                              handleDecisionChange(
                                line.importId,
                                "reason",
                                e.target.value,
                              )
                            }
                            error={
                              decision?.acceptedSource &&
                              (!decision?.reason ||
                                decision?.reason.trim() === "")
                            }
                            sx={{
                              "& .MuiInputBase-input": { fontSize: "0.8rem" },
                            }}
                            fullWidth
                          />
                        ) : (
                          <Typography variant="caption" color="textSecondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        {/* Progress bar */}
        {mismatchedItems > 0 && (
          <Grid item xs={12}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={(decisionsCompletedCount() / mismatchedItems) * 100}
                  color={allDecisionsMade() ? "success" : "primary"}
                />
              </Box>
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ whiteSpace: "nowrap" }}
              >
                {decisionsCompletedCount()} / {mismatchedItems} decisions made
              </Typography>
            </Box>
          </Grid>
        )}

        {displayAlert && (
          <Grid item xs={12}>
            <FormAlert type={alertData.type} message={alertData.message} />
          </Grid>
        )}

        {/* Actions */}
        <Grid item xs={12}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item>
              <ActionButton
                variant="outlined"
                onClick={resetAll}
                disabled={isSubmitting}
              >
                ← Back
              </ActionButton>
            </Grid>
            <Grid item>
              <ActionButton
                variant="contained"
                color="primary"
                startIcon={
                  isSubmitting ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <SendIcon />
                  )
                }
                onClick={handleSubmit}
                disabled={
                  isSubmitting || (mismatchedItems > 0 && !allDecisionsMade())
                }
              >
                {isSubmitting ? "Submitting..." : "Submit Reconciliation"}
              </ActionButton>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER: DONE STEP
  // ─────────────────────────────────────────────────────────────────────────

  const renderDoneStep = () => (
    <Box sx={{ textAlign: "center", py: 6 }}>
      <CheckCircleOutlineIcon
        sx={{ fontSize: 72, color: "success.main", mb: 2 }}
      />
      <Typography variant="h6" fontWeight="bold" mb={1}>
        Reconciliation Complete
      </Typography>
      <Typography
        variant="body2"
        color="textSecondary"
        mb={4}
        sx={{ maxWidth: 480, mx: "auto" }}
      >
        {doneMessage}
      </Typography>
      <Stack direction="row" spacing={2} justifyContent="center">
        <ActionButton variant="outlined" onClick={resetAll}>
          Reconcile Another File
        </ActionButton>
        <ActionButton
          variant="contained"
          color="primary"
          onClick={() => setOpenReconciliationForm(false)}
        >
          Done
        </ActionButton>
      </Stack>
    </Box>
  );

  // ─────────────────────────────────────────────────────────────────────────
  // MAIN RENDER
  // ─────────────────────────────────────────────────────────────────────────

  const stepLabel = {
    [STEP_UPLOAD]: "Step 1 of 2 — Upload Manual Count File",
    [STEP_REVIEW]: "Step 2 of 2 — Review & Resolve Discrepancies",
    [STEP_DONE]: "Complete",
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={11}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
          <BalanceIcon color="primary" />
          <Typography variant="h6">Stock Reconciliation</Typography>
          <Chip
            label={stepLabel[step]}
            size="small"
            variant="outlined"
            color="primary"
            sx={{ ml: 1 }}
          />
        </Box>
        <Typography variant="body2" color="textSecondary" mt={0.5}>
          Upload a manually counted stock file to compare quantities and apply
          adjustments.
        </Typography>
      </Grid>
      <Grid item xs={12} sm={1}>
        <Tooltip title="Close">
          <IconButton
            onClick={() => setOpenReconciliationForm(false)}
            aria-label="close"
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Grid>

      <Grid item xs={12}>
        <Divider />
      </Grid>

      <Grid item xs={12}>
        {step === STEP_UPLOAD && renderUploadStep()}
        {step === STEP_REVIEW && renderReviewStep()}
        {step === STEP_DONE && renderDoneStep()}
      </Grid>
    </Grid>
  );
}

export default StockReconciliation;
